<template>
  <div class="ors">
    <!-- 0. Headline banner -->
    <div class="ors-banner" :class="isPlan ? 'mode-plan' : 'mode-dispatch'">
      <span class="mode-badge" :class="isPlan ? 'tone-warn' : 'tone-ok'">
        {{ isPlan ? 'Dry run · no changes' : 'Acted' }}
      </span>
      <p class="ors-sentence">{{ headlineSentence }}</p>
    </div>

    <div v-if="isPlan" class="ors-plan-hint">
      This is what <strong>dispatch</strong> would do. Run in dispatch mode to act on these directives.
    </div>

    <!-- 1. Catalog health strip -->
    <div v-if="healthChips.length" class="ors-section">
      <div class="ors-kicker">Why it decided · catalog health</div>
      <div class="health-strip">
        <span v-for="chip in healthChips" :key="chip.key" class="health-chip" :class="'tone-' + chip.tone">
          <span class="hc-label">{{ chip.label }}</span>
          <span class="hc-val">{{ chip.value }}</span>
        </span>
      </div>
    </div>

    <!-- 2. Actions taken (dispatch) / Would dispatch (plan) -->
    <div v-if="actionCards.length" class="ors-section">
      <div class="ors-kicker">{{ isPlan ? 'Would dispatch' : 'Actions taken' }}</div>
      <div class="action-cards">
        <div v-for="(card, i) in actionCards" :key="i" class="action-card" :class="'tier-' + card.tier">
          <div class="ac-head">
            <span class="tier-badge" :class="'tier-' + card.tier">{{ card.tierLabel }}</span>
            <span class="ac-title">{{ card.title }}</span>
            <span v-if="card.blockedCpus" class="ac-blocks">blocks {{ card.blockedCpus }} CPU{{ card.blockedCpus === 1 ? '' : 's' }}</span>
          </div>

          <div class="ac-req">
            <span class="req-num">{{ card.requested }}</span> requested
            <span class="req-arrow">→</span>
            <span class="req-num" :class="card.foundTone">{{ card.found }}</span> found
          </div>

          <!-- pipeline trail (dispatch only) -->
          <div v-if="!isPlan && card.pipeline" class="pipeline-trail">
            <template v-for="(step, si) in card.pipeline">
              <div :key="'s' + si" class="trail-step" :class="'is-' + step.status">
                <span class="step-dot"></span>
                <span class="step-copy">
                  <span class="step-name">{{ step.label }} <span class="step-mark">{{ stepMark(step.status) }}</span></span>
                  <span v-if="step.detail" class="step-detail">{{ step.detail }}</span>
                </span>
              </div>
              <span v-if="si < card.pipeline.length - 1" :key="'a' + si" class="trail-arrow">›</span>
            </template>
          </div>

          <!-- resulting candidates (dispatch only) -->
          <div v-if="!isPlan && card.candidates.length" class="ac-candidates">
            <span class="cand-kicker">Queued candidate{{ card.candidates.length === 1 ? '' : 's' }}</span>
            <router-link
              v-for="cand in card.candidates"
              :key="cand.id"
              class="cand-link"
              :to="candidatesLink(card.partType)">
              <span class="cand-model">{{ cand.model }}</span>
              <span v-if="cand.price" class="cand-price">{{ cand.price }}</span>
              <span class="cand-status">{{ cand.status }}</span>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- 3. Planned but not run this quota (dispatch only) -->
    <div v-if="!isPlan && plannedRemaining.length" class="ors-section">
      <div class="ors-kicker">Queued for a future run <small>raise quota to act now</small></div>
      <div class="planned-list">
        <div v-for="(p, i) in plannedRemaining" :key="i" class="planned-row">
          <span class="tier-badge sm" :class="'tier-' + tierKey(p.tier)">{{ tierLabel(p.tier) }}</span>
          <span class="planned-title">{{ (p.count != null ? p.count + '× ' : '') + prettyType(p.part_type) }}</span>
          <span v-if="p.reason" class="planned-reason">{{ p.reason }}</span>
        </div>
      </div>
    </div>

    <!-- 4. Retire — manual decision -->
    <div v-if="retireDecisions.length" class="ors-section">
      <div class="ors-kicker">Retire · needs your decision <small>flagged, not done</small></div>
      <div class="retire-list">
        <div v-for="(r, i) in retireDecisions" :key="i" class="retire-row">
          <span class="retire-badge">Manual</span>
          <span class="retire-title">Retire {{ r.count != null ? r.count + '× ' : '' }}CPU on {{ r.socket || '—' }}</span>
          <span v-if="r.reason" class="retire-reason">{{ r.reason }}</span>
        </div>
      </div>
      <p class="retire-note">This job never archives parts — these orphan sockets are flagged for you to retire manually.</p>
    </div>

    <!-- 5. Technical details -->
    <details class="ors-tech">
      <summary>Technical details <small>raw stats for debugging</small></summary>
      <div class="tech-body">
        <div v-if="webSearchTotal != null" class="tech-line">
          AI web-search calls: <strong>{{ webSearchTotal }}</strong>
        </div>
        <div v-if="techChips.length" class="tech-chips">
          <span v-for="chip in techChips" :key="chip.key" class="tech-chip">
            <span class="tc-key">{{ chip.key }}</span>
            <span class="tc-val">{{ chip.value }}</span>
          </span>
        </div>
        <div v-for="prev in aiPreviews" :key="prev.ns" class="ai-preview">
          <span class="ai-preview-head">{{ prev.ns }} · AI output preview</span>
          <pre>{{ prev.text }}</pre>
        </div>
      </div>
    </details>
  </div>
</template>

<script>
const TYPE_LABELS = {
  cpu: 'CPU', motherboard: 'Motherboard', ram: 'RAM', storage: 'Storage',
  gpu: 'GPU', psu: 'PSU', case: 'Case'
}
const TIER_RANK = { 'platform-keystone': 3, 'subpath-keystone': 2, variety: 1 }
const TIER_LABELS = { 'platform-keystone': 'Platform keystone', 'subpath-keystone': 'Sub-path keystone', variety: 'Variety' }
const STAGES = [
  { key: 'search', label: 'Search' },
  { key: 'scrape', label: 'Scrape' },
  { key: 'specs', label: 'Specs' },
  { key: 'validate', label: 'Validate' }
]

export default {
  name: 'OnboardingRunSummary',
  props: {
    run: { type: Object, default: () => ({}) }
  },
  computed: {
    stats () {
      return (this.run && this.run.stats && typeof this.run.stats === 'object') ? this.run.stats : {}
    },
    record () {
      const r = this.stats.record
      return (r && typeof r === 'object') ? r : {}
    },
    reportSummary () {
      const s = this.record.report_summary
      return (s && typeof s === 'object') ? s : {}
    },
    findings () {
      return Array.isArray(this.record.findings) ? this.record.findings : []
    },
    decisions () {
      const d = this.record.decisions
      return (d && typeof d === 'object') ? d : {}
    },
    onboardDecisions () { return Array.isArray(this.decisions.onboard) ? this.decisions.onboard : [] },
    retireDecisions () { return Array.isArray(this.decisions.retire) ? this.decisions.retire : [] },
    anyDispatched () { return this.findings.some((f) => f && f.dispatched) },
    isPlan () {
      const mode = String(this.stats.mode || '').toLowerCase()
      if (mode === 'plan') return true
      if (mode === 'dispatch') return false
      return !this.anyDispatched
    },
    directivesDispatched () { return this.numv(this.stats.directivesDispatched) },
    onboardDirectives () {
      const v = this.stats.onboardDirectives
      return v != null ? this.numv(v) : this.onboardDecisions.length
    },
    candidatesQueued () { return this.numv(this.stats.candidatesQueued) },
    headlineSentence () {
      if (this.isPlan) {
        const m = this.onboardDirectives
        const r = this.retireDecisions.length
        const retirePart = r ? ` and would flag ${r} socket${r === 1 ? '' : 's'} to retire` : ''
        return `Dry run — dispatch would act on ${m} directive${m === 1 ? '' : 's'}${retirePart}. No changes were made.`
      }
      const n = this.directivesDispatched || this.dispatchedFindings.length
      const m = this.onboardDirectives
      const k = this.candidatesQueued || this.dispatchedFindings.reduce((acc, d) => acc + this.candidateList(d.finding).length, 0)
      let tail = '.'
      if (k > 0) tail = this.allValidated ? ', all reached validated (ready to import).' : ', awaiting review.'
      return `Dispatched ${n} of ${m} directive${m === 1 ? '' : 's'} → queued ${k} candidate${k === 1 ? '' : 's'}${tail}`
    },
    allValidated () {
      const list = this.dispatchedFindings
      if (!list.length) return false
      return list.every((d) => {
        const v = this.nsStageBlock(d.ns, 'validate')
        const validated = this.numv(v.validated)
        const blocked = this.numv(v.blocked)
        return validated > 0 && blocked === 0
      })
    },
    healthChips () {
      const s = this.reportSummary
      if (!Object.keys(s).length) return []
      const chips = []
      const push = (key, label, val, tone) => {
        if (val == null) return
        chips.push({ key, label, value: this.numv(val), tone: tone || 'neutral' })
      }
      push('sockets', 'Sockets', s.sockets, 'neutral')
      push('viable', 'Viable platforms', s.viablePlatforms, 'ok')
      push('orphans', 'Orphans', s.orphanSockets, this.numv(s.orphanSockets) > 0 ? 'bad' : 'neutral')
      push('ff', 'Form factors', s.formFactors, 'neutral')
      push('keystones', 'Keystone breaks', s.keystones, 'bad')
      push('variety', 'Variety gaps', s.varietyGaps, 'warn')
      push('healthy', 'Healthy', s.healthy, 'ok')
      return chips
    },
    dispatchedFindings () {
      let idx = 0
      return this.findings.filter((f) => f && f.dispatched).map((f) => {
        idx += 1
        return { finding: f, ns: this.directiveNs(f, idx) }
      })
    },
    actionCards () {
      if (!this.isPlan) {
        return this.dispatchedFindings.map((d) => this.dispatchCard(d.finding, d.ns))
      }
      // Plan preview: the ranked onboard decisions, no pipeline/candidates.
      return this.onboardDecisions.map((dec) => this.previewCard(dec))
    },
    plannedRemaining () {
      const n = this.directivesDispatched
      if (!n) return this.onboardDecisions.slice(this.dispatchedFindings.length)
      return this.onboardDecisions.slice(n)
    },
    webSearchTotal () {
      let total = null
      this.dispatchedFindings.forEach((d) => {
        const calls = this.getNs(d.ns, 'search', 'aiWebSearchCalls')
        if (calls != null) total = (total || 0) + this.numv(calls)
      })
      return total
    },
    aiPreviews () {
      const out = []
      this.dispatchedFindings.forEach((d) => {
        const prev = this.getNs(d.ns, 'search', 'aiOutputPreview')
        if (prev) out.push({ ns: d.ns, text: String(prev) })
      })
      return out
    },
    techChips () {
      const out = []
      this.flatten(this.stats).forEach((s) => {
        if (!/^d\d+\./i.test(s.key)) return
        if (/aioutputpreview$/i.test(s.key)) return
        if (s.value == null || typeof s.value === 'object') return
        out.push({ key: s.key, value: String(s.value) })
      })
      return out
    }
  },
  methods: {
    // ---- card builders ----
    dispatchCard (f, ns) {
      const dir = (f && f.directive) || {}
      const tier = this.tierKey(f.tier || dir.tier || (dir.priority && dir.priority.tier))
      const count = f.requested != null ? f.requested : dir.count
      const partType = f.part_type || dir.part_type
      const found = this.numv(f.found)
      const requested = this.numv(f.requested)
      return {
        tier,
        tierLabel: this.tierLabel(tier),
        title: `${count != null ? count + '× ' : ''}${this.prettyType(partType)}`,
        partType,
        blockedCpus: this.blockedCount(dir),
        requested: f.requested != null ? f.requested : '—',
        found: f.found != null ? f.found : 0,
        foundTone: found === 0 ? 'tone-bad' : (requested && found < requested ? 'tone-warn' : 'tone-ok'),
        pipeline: STAGES.map((st) => this.stepModel(st.label, ns, st.key)),
        candidates: this.candidateList(f).map((c) => this.candidateModel(c))
      }
    },
    previewCard (dec) {
      const tier = this.tierKey(dec.tier)
      return {
        tier,
        tierLabel: this.tierLabel(tier),
        title: `${dec.count != null ? dec.count + '× ' : ''}${this.prettyType(dec.part_type)}`,
        partType: dec.part_type,
        blockedCpus: this.blockedCount(dec),
        requested: dec.count != null ? dec.count : '—',
        found: '—',
        foundTone: 'tone-muted',
        pipeline: null,
        candidates: []
      }
    },

    // ---- pipeline steps ----
    stepModel (label, ns, stage) {
      const block = this.nsStageBlock(ns, stage)
      const keys = Object.keys(block || {})
      if (!keys.length) return { label, status: 'skip', detail: 'skipped' }
      const parts = []
      keys.forEach((k) => {
        const v = block[k]
        if (typeof v === 'number') parts.push(`${v} ${this.spacey(k)}`)
      })
      let status = 'ok'
      const blocked = this.numv(block.blocked)
      const failed = this.numv(block.failed)
      const skipped = this.numv(block.skipped)
      if (stage === 'validate' && blocked > 0) status = 'bad'
      else if (failed > 0) status = 'warn'
      else if (skipped > 0 && parts.length === 1) status = 'warn'
      return { label, status, detail: parts.join(', ') }
    },
    stepMark (status) {
      if (status === 'ok') return '✓'
      if (status === 'bad') return '✕'
      if (status === 'warn') return '!'
      return '·'
    },

    // ---- namespaced stat access (nested or flat) ----
    nsStageBlock (ns, stage) {
      const block = this.stats[ns]
      if (block && typeof block === 'object' && block[stage] && typeof block[stage] === 'object') {
        return block[stage]
      }
      const out = {}
      const prefix = `${ns}.${stage}.`
      Object.keys(this.stats).forEach((k) => {
        if (k.indexOf(prefix) === 0) out[k.slice(prefix.length)] = this.stats[k]
      })
      return out
    },
    getNs (ns, stage, field) {
      const block = this.nsStageBlock(ns, stage)
      return block && field in block ? block[field] : undefined
    },
    directiveNs (f, idx) {
      const dir = (f && f.directive) || {}
      const id = dir.id || (f && f.id)
      if (typeof id === 'string' && /^d\d+$/i.test(id)) return id.toLowerCase()
      if (typeof id === 'number') return 'd' + id
      return 'd' + idx
    },
    candidateList (f) {
      const ids = f && f.candidateIds
      return Array.isArray(ids) ? ids : []
    },
    candidateModel (entry) {
      if (entry && typeof entry === 'object') {
        return {
          id: entry.id != null ? entry.id : entry.candidateId,
          model: entry.model || entry.name || `#${entry.id != null ? entry.id : '?'}`,
          price: entry.price != null ? this.money(entry.price) : '',
          status: entry.status || 'queued'
        }
      }
      return { id: entry, model: `#${entry}`, price: '', status: 'queued' }
    },

    // ---- tier / type ----
    tierKey (t) {
      const k = String(t || '').toLowerCase()
      return TIER_RANK[k] ? k : 'variety'
    },
    tierLabel (t) { return TIER_LABELS[this.tierKey(t)] || 'Variety' },
    blockedCount (dir) {
      const b = dir && (dir.blocked_cpus != null ? dir.blocked_cpus : dir.blockedCpus)
      if (Array.isArray(b)) return b.length
      return this.numv(b)
    },
    prettyType (type) {
      const key = String(type || '').toLowerCase()
      return TYPE_LABELS[key] || (key ? key.charAt(0).toUpperCase() + key.slice(1) : '—')
    },
    candidatesLink (partType) {
      const q = partType ? `?type=${encodeURIComponent(partType)}` : ''
      return `/users/candidates${q}`
    },

    // ---- utils ----
    numv (v) {
      const n = Number(v)
      return (typeof v !== 'undefined' && v !== null && !isNaN(n)) ? n : 0
    },
    money (n) {
      if (n == null || n === '' || isNaN(Number(n))) return ''
      return '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })
    },
    spacey (k) {
      return String(k || '').replace(/[_.]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
    },
    flatten (stats, prefix) {
      if (!stats || typeof stats !== 'object') return []
      let out = []
      Object.keys(stats).forEach((key) => {
        const value = stats[key]
        const full = prefix ? `${prefix}.${key}` : key
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          out = out.concat(this.flatten(value, full))
        } else {
          out.push({ key: full, value: Array.isArray(value) ? value.join(', ') : value })
        }
      })
      return out
    }
  }
}
</script>

<style scoped>
.ors { color: #cfd2da; }

/* Banner */
.ors-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid rgba(67, 72, 87, 0.86);
  border-left-width: 3px;
  border-radius: 8px;
  background: rgba(12, 16, 24, 0.6);
}

.ors-banner.mode-dispatch { border-left-color: #1bc98e; }
.ors-banner.mode-plan { border-left-color: #e0c07e; }

.mode-badge {
  flex: none;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.mode-badge.tone-ok { border: 1px solid rgba(27, 201, 142, 0.45); background: rgba(27, 201, 142, 0.12); color: #89dfbd; }
.mode-badge.tone-warn { border: 1px solid rgba(224, 192, 126, 0.45); background: rgba(224, 192, 126, 0.12); color: #e0c07e; }

.ors-sentence { margin: 0; color: #e7ebf3; font-size: 13.5px; font-weight: 600; line-height: 1.45; }

.ors-plan-hint {
  margin-top: 10px;
  padding: 9px 12px;
  border: 1px dashed rgba(224, 192, 126, 0.5);
  border-radius: 8px;
  background: rgba(224, 192, 126, 0.07);
  color: #d9c79a;
  font-size: 12.5px;
}

.ors-plan-hint strong { color: #efd9a6; }

/* Sections */
.ors-section { margin-top: 18px; }

.ors-kicker {
  margin-bottom: 9px;
  color: #1997c6;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.ors-kicker small { margin-left: 6px; color: #6f788b; font-weight: 700; text-transform: none; letter-spacing: 0; }

/* Health strip */
.health-strip { display: flex; flex-wrap: wrap; gap: 8px; }

.health-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 11px;
  border: 1px solid rgba(67, 72, 87, 0.8);
  border-radius: 8px;
  background: rgba(20, 25, 35, 0.55);
}

.hc-label { color: #8f98aa; font-size: 11px; font-weight: 700; }
.hc-val { color: #eaf0f7; font-size: 14px; font-weight: 800; }
.health-chip.tone-ok { border-color: rgba(27, 201, 142, 0.32); }
.health-chip.tone-ok .hc-val { color: #89dfbd; }
.health-chip.tone-warn { border-color: rgba(224, 192, 126, 0.34); }
.health-chip.tone-warn .hc-val { color: #e0c07e; }
.health-chip.tone-bad { border-color: rgba(232, 79, 106, 0.4); }
.health-chip.tone-bad .hc-val { color: #f0a1af; }

/* Action cards */
.action-cards { display: grid; gap: 10px; }

.action-card {
  padding: 13px 14px;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-left-width: 3px;
  border-radius: 10px;
  background: rgba(12, 16, 24, 0.5);
}

.action-card.tier-platform-keystone { border-left-color: #e8536a; }
.action-card.tier-subpath-keystone { border-left-color: #e8924f; }
.action-card.tier-variety { border-left-color: #e0c07e; }

.ac-head { display: flex; align-items: center; flex-wrap: wrap; gap: 9px; }

.tier-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.tier-badge.sm { padding: 2px 7px; font-size: 9px; }
.tier-badge.tier-platform-keystone { border: 1px solid rgba(232, 83, 106, 0.55); background: rgba(232, 83, 106, 0.16); color: #f4889a; }
.tier-badge.tier-subpath-keystone { border: 1px solid rgba(232, 146, 79, 0.5); background: rgba(232, 146, 79, 0.14); color: #efb27e; }
.tier-badge.tier-variety { border: 1px solid rgba(224, 192, 126, 0.45); background: rgba(224, 192, 126, 0.12); color: #e0c07e; }

.ac-title { color: #eaf0f7; font-size: 14.5px; font-weight: 800; }
.ac-blocks { color: #f0a1af; font-size: 11.5px; font-weight: 700; }

.ac-req { margin-top: 8px; color: #9aa3b4; font-size: 12.5px; }
.req-num { color: #dce1eb; font-weight: 800; }
.req-num.tone-ok { color: #89dfbd; }
.req-num.tone-warn { color: #e0c07e; }
.req-num.tone-bad { color: #f0a1af; }
.req-arrow { margin: 0 6px; color: #6f788b; }

/* Pipeline trail */
.pipeline-trail {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 6px;
  margin-top: 12px;
}

.trail-step { display: flex; align-items: flex-start; gap: 7px; min-width: 0; }

.step-dot {
  margin-top: 4px;
  width: 9px;
  height: 9px;
  flex: none;
  border-radius: 50%;
  background: #4a5163;
}

.trail-step.is-ok .step-dot { background: #1bc98e; box-shadow: 0 0 8px rgba(27, 201, 142, 0.5); }
.trail-step.is-warn .step-dot { background: #e0c07e; }
.trail-step.is-bad .step-dot { background: #e8536a; box-shadow: 0 0 8px rgba(232, 83, 106, 0.5); }
.trail-step.is-skip .step-dot { background: #3a3f4d; }

.step-copy { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.step-name { color: #dce1eb; font-size: 12px; font-weight: 700; }
.trail-step.is-skip .step-name { color: #6f788b; }
.step-mark { font-weight: 800; }
.trail-step.is-ok .step-mark { color: #89dfbd; }
.trail-step.is-bad .step-mark { color: #f0a1af; }
.trail-step.is-warn .step-mark { color: #e0c07e; }
.step-detail { color: #8f98aa; font-size: 11px; }
.trail-arrow { margin-top: 1px; color: #545b6d; font-size: 16px; }

/* Candidates */
.ac-candidates { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 12px; }
.cand-kicker { color: #7f899b; font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; }

.cand-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border: 1px solid rgba(25, 151, 198, 0.4);
  border-radius: 999px;
  background: rgba(25, 151, 198, 0.08);
  color: #9bdff5;
  font-size: 12px;
  text-decoration: none;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.cand-link:hover { border-color: #1997c6; background: rgba(25, 151, 198, 0.16); color: #eef8fc; }
.cand-model { font-weight: 700; }
.cand-price { color: #cfd2da; }
.cand-status {
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(58, 63, 77, 0.6);
  color: #aeb5c5;
  font-size: 9.5px;
  font-weight: 800;
  text-transform: uppercase;
}

/* Planned */
.planned-list { display: grid; gap: 7px; }

.planned-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 11px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 8px;
  background: rgba(20, 25, 35, 0.4);
}

.planned-title { color: #dce1eb; font-size: 13px; font-weight: 700; }
.planned-reason { color: #8f98aa; font-size: 11.5px; }

/* Retire */
.retire-list { display: grid; gap: 7px; }

.retire-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 11px;
  border: 1px solid rgba(232, 146, 79, 0.32);
  border-radius: 8px;
  background: rgba(232, 146, 79, 0.07);
}

.retire-badge {
  flex: none;
  padding: 2px 8px;
  border: 1px solid rgba(232, 146, 79, 0.5);
  border-radius: 999px;
  color: #efb27e;
  font-size: 9.5px;
  font-weight: 800;
  text-transform: uppercase;
}

.retire-title { color: #e7ebf3; font-size: 13px; font-weight: 700; }
.retire-reason { color: #8f98aa; font-size: 11.5px; }
.retire-note { margin: 8px 0 0; color: #767f92; font-size: 11.5px; font-style: italic; }

/* Technical details */
.ors-tech {
  margin-top: 20px;
  padding: 10px 12px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 8px;
  background: rgba(9, 13, 20, 0.4);
}

.ors-tech > summary {
  cursor: pointer;
  color: #9aa3b4;
  font-size: 12px;
  font-weight: 700;
  list-style: revert;
}

.ors-tech > summary small { margin-left: 6px; color: #6f788b; font-weight: 600; }
.tech-body { margin-top: 12px; }
.tech-line { margin-bottom: 8px; color: #a4acbc; font-size: 12px; }
.tech-line strong { color: #dce1eb; }
.tech-chips { display: flex; flex-wrap: wrap; gap: 6px; }

.tech-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 6px;
  background: rgba(20, 25, 35, 0.5);
  font-size: 11px;
}

.tc-key { color: #7f899b; font-family: monospace; }
.tc-val { color: #cfd2da; font-weight: 700; }

.ai-preview { margin-top: 12px; }
.ai-preview-head { color: #7f899b; font-size: 10.5px; font-weight: 800; text-transform: uppercase; }

.ai-preview pre {
  margin: 6px 0 0;
  padding: 10px;
  max-height: 220px;
  overflow: auto;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.3);
  color: #b8c0d0;
  font-size: 11px;
  white-space: pre-wrap;
}
</style>
